<script setup lang="ts">
import { computed, inject, ref, watch } from 'vue'
import BbelField from '@/views/builder/components/bbel/BbelField.vue'
import { formatBbelBody, type FormatLang } from '@/views/builder/components/bbel/bbel-format'
import { BBEL_SCOPE_KEY, EMPTY_BBEL_SCOPE, type BbelScopeContext } from '@/views/builder/composables/bbel-context'
import type { BbelFieldLang, ScopeDescriptor } from '@/types/bbel-types'

/**
 * Text body editor shared by the workflow builder's HTTP service and the connection-template
 * handler pane: content-type-derived syntax highlighting with a session override, and a Format
 * action that pretty-prints JSON/XML around the `{{ }}` slots (bbel-format).
 */
const props = defineProps<{
  modelValue: string
  /** The request's content type — drives the editor's default language. */
  contentType?: string
  label?: string
  /** Explicit BBEL scope; falls back to the builder's injected scope context. */
  scope?: ScopeDescriptor
  placeholder?: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

// #5683 — the body is a BBEL field (scope from the builder's provide when not passed explicitly).
const bbelScopeCtx = inject<BbelScopeContext | undefined>(BBEL_SCOPE_KEY, undefined)
const scope = computed<ScopeDescriptor>(() => props.scope ?? bbelScopeCtx?.value ?? EMPTY_BBEL_SCOPE)

// Editor language: derived from content type, user-overridable per session. The override is
// ephemeral by design — persisting it would be a model contract change for a pure editor
// preference; the content type remains the source of truth.
const derivedLang = computed<BbelFieldLang>(() => {
  const ct = props.contentType ?? ''
  if (ct.includes('json')) return 'json'
  if (ct.includes('xml') || ct === 'text/html') return 'xml'
  return 'text'
})

const langOverride = ref<BbelFieldLang | null>(null)
const lang = computed(() => langOverride.value ?? derivedLang.value)

// Picking a new content type restates intent — the derived language takes over again.
watch(() => props.contentType, () => (langOverride.value = null))

const formatError = ref(false)

function format() {
  const out = formatBbelBody(props.modelValue, lang.value as FormatLang)
  formatError.value = out === null
  if (out !== null && out !== props.modelValue) emit('update:modelValue', out)
}

function update(value: string) {
  formatError.value = false
  emit('update:modelValue', value)
}
</script>

<template>
  <div class="mb-3">
    <div class="d-flex align-items-center justify-content-between mb-1">
      <label class="form-label mb-0">{{ label ?? 'Request Body' }}</label>
      <div class="d-flex align-items-center gap-2">
        <button
          v-if="lang !== 'text'"
          type="button"
          class="btn btn-outline-secondary btn-sm"
          :title="`Pretty-print as ${lang.toUpperCase()} — {{ }} expressions are left untouched`"
          @click="format"
        >
          <i class="bi bi-text-indent-left"></i> Format
        </button>
        <select
          class="form-select form-select-sm w-auto"
          :value="lang"
          title="Editor syntax highlighting"
          @change="langOverride = ($event.target as HTMLSelectElement).value as BbelFieldLang"
        >
          <option value="text">Text</option>
          <option value="json">JSON</option>
          <option value="xml">XML</option>
        </select>
      </div>
    </div>
    <BbelField
      :model-value="modelValue"
      @update:model-value="update"
      :scope="scope"
      field-shape="body"
      :lang="lang"
      :placeholder="placeholder ?? 'Enter request body content...'"
    />
    <div v-if="formatError" class="form-text text-danger">
      Can't format — the body isn't valid {{ lang.toUpperCase() }} (expressions aside).
    </div>
  </div>
</template>
